import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Clock, CheckCircle2, XCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import MaterialItemSelector from '../MaterialItemSelector';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../lib/format';

// ── Shared ──
const INDENT_BADGE = {
  pending: { cls: 'bg-amber-100 text-amber-700', Icon: Clock, label: 'Pending' },
  approved: { cls: 'bg-blue-100 text-blue-700', Icon: CheckCircle2, label: 'Approved' },
  issued: { cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2, label: 'Issued' },
  partial: { cls: 'bg-orange-100 text-orange-700', Icon: Clock, label: 'Partial' },
  rejected: { cls: 'bg-red-100 text-red-700', Icon: XCircle, label: 'Rejected' },
  closed: { cls: 'bg-gray-100 text-gray-600', Icon: Check, label: 'Closed' },
};

function StatusPill({ status, map = INDENT_BADGE }) {
  const b = map[status] || { cls: 'bg-gray-100 text-gray-600', Icon: Clock, label: status };
  const Icon = b.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${b.cls}`} data-testid={`status-${status}`}>
      <Icon className="w-3 h-3" /> {b.label}
    </span>
  );
}

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="modal-close"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function varianceClass(pct) {
  const a = Math.abs(pct || 0);
  if (a === 0) return 'text-emerald-600';
  if (a <= 5) return 'text-amber-600';
  return 'text-red-600 font-semibold';
}

// ── Slice 1B — Material Returns ──
export function ReturnsTab({ projectId, projects = [], compact = false }) {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [issues, setIssues] = useState([]);
  const [fProject, setFProject] = useState(projectId || '');
  const [fIssue, setFIssue] = useState('');
  const [fItems, setFItems] = useState([]);
  const [fCondition, setFCondition] = useState('good');
  const [fReason, setFReason] = useState('');
  const [fReturnedBy, setFReturnedBy] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      try {
        const data = await apiGet('/stores/return', projectId ? { project_id: projectId } : {});
        if (active) setReturns(data.items || []);
      } catch { /* noop */ } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [projectId, reloadKey]);

  useEffect(() => {
    if (!fProject) return undefined;
    let active = true;
    apiGet('/stores/issue', { project_id: fProject }).then((d) => { if (active) setIssues(d.items || []); }).catch(() => {});
    return () => { active = false; };
  }, [fProject]);

  const openCreate = () => {
    setFProject(projectId || ''); setFItems([]); setFReason(''); setFIssue(''); setFCondition('good');
    setShowCreate(true);
  };

  const submit = async () => {
    if (!fProject) { toast.error('Pick a project'); return; }
    if (!fItems.length) { toast.error('Add at least one item'); return; }
    setSubmitting(true);
    try {
      await apiPost('/stores/return', {
        project_id: fProject,
        original_issue_id: fIssue || undefined,
        returned_by: fReturnedBy || undefined,
        items: fItems.map((i) => ({ boq_id: i.boq_id, returned_qty: i.quantity, condition: fCondition, reason: fReason || undefined })),
      });
      toast.success('Return recorded');
      setShowCreate(false); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record return');
    } finally { setSubmitting(false); }
  };

  const condBadge = (c) => ({ good: 'bg-emerald-100 text-emerald-700', damaged: 'bg-red-100 text-red-700', partial: 'bg-amber-100 text-amber-700' }[c] || 'bg-gray-100 text-gray-600');

  return (
    <div className="space-y-4" data-testid="stores-returns-tab">
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500">{returns.length} Material Returns</div>
        <Button onClick={openCreate} data-testid="new-return-btn">
          <Plus className="w-4 h-4 mr-2" /> New Return
        </Button>
      </div>
      <Card><CardContent className="p-0">
        {compact ? (
          <div className="space-y-2 p-2" data-testid="returns-card-list">
            {loading ? <div className="text-center py-6 text-slate-400 text-sm">Loading…</div>
              : returns.length === 0 ? <div className="text-center py-6 text-slate-400 text-sm">No returns yet</div>
              : returns.map((r) => (
                <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`return-card-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-semibold">{r.return_no}</span>
                    <span className="text-xs font-semibold text-slate-600">₹{(r.total_value || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">{r.project_name} · {r.returned_by || '—'}</div>
                  <div className="mt-1.5">
                    {[...new Set((r.items || []).map((i) => i.condition))].map((c) => (
                      <span key={c} className={`inline-block mr-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${condBadge(c)}`}>{c}</span>
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1.5">{formatDate(r.created_at)}</div>
                </div>
              ))}
          </div>
        ) : (
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Return No</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Project</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Returned By</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Conditions</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Value</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
            </tr></thead>
            <tbody className="divide-y">
              {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-500">Loading…</td></tr>
                : returns.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-500">No returns yet</td></tr>
                  : returns.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50" data-testid={`return-row-${r.id}`}>
                      <td className="px-4 py-3 font-mono font-medium">{r.return_no}</td>
                      <td className="px-4 py-3">{r.project_name}</td>
                      <td className="px-4 py-3">{r.returned_by}</td>
                      <td className="px-4 py-3">
                        {[...new Set((r.items || []).map((i) => i.condition))].map((c) => (
                          <span key={c} className={`inline-block mr-1 px-2 py-0.5 rounded-full text-xs font-medium ${condBadge(c)}`}>{c}</span>
                        ))}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">₹{(r.total_value || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{formatDate(r.created_at)}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        )}
      </CardContent></Card>

      {showCreate && (
        <Modal title="New Material Return" onClose={() => setShowCreate(false)} testid="return-create-modal">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Project *</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={fProject} onChange={(e) => setFProject(e.target.value)} data-testid="return-form-project">
                  <option value="">Select project…</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Against issue (optional)</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={fIssue} onChange={(e) => setFIssue(e.target.value)} data-testid="return-form-issue">
                  <option value="">— None —</option>
                  {issues.map((i) => <option key={i.id} value={i.id}>{i.issue_no}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Condition *</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={fCondition} onChange={(e) => setFCondition(e.target.value)} data-testid="return-form-condition">
                  <option value="good">Good (back to stock)</option>
                  <option value="damaged">Damaged (write-off)</option>
                  <option value="partial">Partial</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Returned by</label>
                <Input value={fReturnedBy} onChange={(e) => setFReturnedBy(e.target.value)} data-testid="return-form-returnedby" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Items *</label>
              <MaterialItemSelector projectId={fProject} mode="return" selectedItems={fItems} onItemsChange={setFItems} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reason</label>
              <Input value={fReason} onChange={(e) => setFReason(e.target.value)} placeholder="e.g. excess / wrong spec / damaged" data-testid="return-form-reason" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="return-form-submit">{submitting ? 'Saving…' : 'Record Return'}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Slice 1C — Stocktake ──
export function StocktakeTab({ projectId, projects = [], compact = false }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [fProject, setFProject] = useState(projectId || '');
  const [fCountedBy, setFCountedBy] = useState(user?.name || '');
  const [fItems, setFItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const canApprove = !!user && (user.role === 'super_admin' || ['MD', 'CEO', 'PM', 'manager'].includes(user.role));
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      try {
        const data = await apiGet('/stores/stocktake', projectId ? { project_id: projectId } : {});
        if (active) setList(data.items || []);
      } catch { /* noop */ } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [projectId, reloadKey]);

  const openCreate = () => { setFProject(projectId || ''); setFItems([]); setShowCreate(true); };

  const submit = async () => {
    if (!fProject) { toast.error('Pick a project'); return; }
    if (!fItems.length) { toast.error('Add at least one item to count'); return; }
    setSubmitting(true);
    try {
      await apiPost('/stores/stocktake', {
        project_id: fProject, counted_by: fCountedBy || undefined,
        items: fItems.map((i) => ({ boq_id: i.boq_id, physical_qty: i.quantity })),
      });
      toast.success('Stocktake recorded — pending approval');
      setShowCreate(false); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record stocktake');
    } finally { setSubmitting(false); }
  };

  const approve = async (id) => {
    try { await apiPut(`/stores/stocktake/${id}/approve`, {}); toast.success('Stocktake approved & adjustments applied'); reload(); if (detail?.id === id) setDetail(null); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not approve'); }
  };

  const exportCsv = (stk) => {
    const rows = [['Item', 'Unit', 'System Qty', 'Physical Qty', 'Variance', 'Variance %', 'Remarks']];
    (stk.items || []).forEach((it) => rows.push([it.description, it.unit, it.system_qty, it.physical_qty, it.variance, it.variance_pct, it.remarks || '']));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${stk.stocktake_no.replace(/\//g, '-')}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const stkBadge = { pending_approval: { cls: 'bg-amber-100 text-amber-700', Icon: Clock, label: 'Pending approval' }, approved: { cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2, label: 'Approved' } };

  return (
    <div className="space-y-4" data-testid="stores-stocktake-tab">
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500">{list.length} Stocktakes</div>
        <Button onClick={openCreate} data-testid="new-stocktake-btn">
          <Plus className="w-4 h-4 mr-2" /> New Stocktake
        </Button>
      </div>
      <Card><CardContent className="p-0">
        {compact ? (
          <div className="space-y-2 p-2" data-testid="stocktake-card-list">
            {loading ? <div className="text-center py-6 text-slate-400 text-sm">Loading…</div>
              : list.length === 0 ? <div className="text-center py-6 text-slate-400 text-sm">No stocktakes yet</div>
              : list.map((s) => (
                <button key={s.id} onClick={() => setDetail(s)} className="w-full text-left bg-white border border-slate-200 rounded-lg p-3" data-testid={`stocktake-card-${s.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-semibold">{s.stocktake_no}</span>
                    <StatusPill status={s.status} map={stkBadge} />
                  </div>
                  <div className="text-xs text-slate-600 mt-1">{s.project_name}</div>
                  <div className="text-[11px] text-slate-500 mt-1.5">{s.total_items} items · {s.items_with_variance} with variance</div>
                  {canApprove && s.status === 'pending_approval' && (
                    <div className="mt-2">
                      <Button size="sm" className="w-full" onClick={(e) => { e.stopPropagation(); approve(s.id); }} data-testid={`stocktake-approve-${s.id}`}>Approve &amp; apply adjustments</Button>
                    </div>
                  )}
                </button>
              ))}
          </div>
        ) : (
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Stocktake No</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Project</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Items</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">With Variance</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr></thead>
            <tbody className="divide-y">
              {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-500">Loading…</td></tr>
                : list.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-500">No stocktakes yet</td></tr>
                  : list.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50" data-testid={`stocktake-row-${s.id}`}>
                      <td className="px-4 py-3 font-mono font-medium"><button className="text-blue-600 hover:underline" onClick={() => setDetail(s)} data-testid={`stocktake-view-${s.id}`}>{s.stocktake_no}</button></td>
                      <td className="px-4 py-3">{s.project_name}</td>
                      <td className="px-4 py-3 text-right">{s.total_items}</td>
                      <td className="px-4 py-3 text-right">{s.items_with_variance}</td>
                      <td className="px-4 py-3"><StatusPill status={s.status} map={stkBadge} /></td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => exportCsv(s)} data-testid={`stocktake-export-${s.id}`}>Export</Button>
                        {canApprove && s.status === 'pending_approval' && (
                          <Button size="sm" onClick={() => approve(s.id)} data-testid={`stocktake-approve-${s.id}`}>Approve</Button>
                        )}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        )}
      </CardContent></Card>

      {showCreate && (
        <Modal title="New Stocktake" onClose={() => setShowCreate(false)} testid="stocktake-create-modal">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Project *</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={fProject} onChange={(e) => setFProject(e.target.value)} data-testid="stocktake-form-project">
                  <option value="">Select project…</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Counted by</label>
                <Input value={fCountedBy} onChange={(e) => setFCountedBy(e.target.value)} data-testid="stocktake-form-countedby" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Enter physical count per item *</label>
              <p className="text-xs text-gray-500 mb-2">The qty you enter is the physical count; the system qty &amp; variance are computed on save.</p>
              <MaterialItemSelector projectId={fProject} mode="stocktake" selectedItems={fItems} onItemsChange={setFItems} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="stocktake-form-submit">{submitting ? 'Saving…' : 'Record Stocktake'}</Button>
            </div>
          </div>
        </Modal>
      )}

      {detail && (
        <Modal title={`Stocktake ${detail.stocktake_no}`} onClose={() => setDetail(null)} testid="stocktake-detail-modal">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <StatusPill status={detail.status} map={stkBadge} />
              <Button size="sm" variant="outline" onClick={() => exportCsv(detail)}>Export CSV</Button>
            </div>
            <table className="w-full text-sm border-t">
              <thead><tr className="text-left text-gray-500 border-b">
                <th className="py-2">Item</th><th className="text-right">System</th><th className="text-right">Physical</th><th className="text-right">Variance</th><th className="text-right">%</th>
              </tr></thead>
              <tbody>
                {(detail.items || []).map((it, i) => (
                  <tr key={i} className="border-b" data-testid={`stocktake-detail-line-${i}`}>
                    <td className="py-2">{it.description}</td>
                    <td className="text-right">{it.system_qty} {it.unit}</td>
                    <td className="text-right">{it.physical_qty}</td>
                    <td className={`text-right ${varianceClass(it.variance_pct)}`}>{it.variance}</td>
                    <td className={`text-right ${varianceClass(it.variance_pct)}`}>{it.variance_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {canApprove && detail.status === 'pending_approval' && (
              <div className="flex justify-end"><Button onClick={() => approve(detail.id)} data-testid="stocktake-detail-approve">Approve &amp; apply adjustments</Button></div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
